import React from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import './SkiffTrailersPage.css'

interface SkiffTrailerModel {
  model: string;
  capacity: number;
  boatLength: string;
  overallLength: string;
  bowEyeMin: string;
  bowEyeMax: string;
  gvwr: number;
  tongueJack: string;
  axles: number;
  brakes: string;
  tireSize: string;
  overallWidth: string;
  betweenFenders: string;
}

const SkiffTrailersPage: React.FC = () => {
  // Aluminum Jon Boat Trailers
  const aluminumJonBoatModels: SkiffTrailerModel[] = [
    {
      model: "VAB-1025",
      capacity: 1025,
      boatLength: "12'-14'",
      overallLength: "15'",
      bowEyeMin: "8'2\"",
      bowEyeMax: "12'8\"",
      gvwr: 1230,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "4.80 x 12 LRC 5H",
      overallWidth: "60\"",
      betweenFenders: "42\""
    },
    {
      model: "VAB-1325",
      capacity: 1325,
      boatLength: "14'-15'",
      overallLength: "18'1\"",
      bowEyeMin: "8'6\"",
      bowEyeMax: "15'6\"",
      gvwr: 1535,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "4.80 x 12 LRC 5H",
      overallWidth: "60\"",
      betweenFenders: "42\""
    }
  ];

  // Aluminum Single Axle Skiff Trailers
  const aluminumSingleAxleSkiffModels: SkiffTrailerModel[] = [
    {
      model: "VASK-1800",
      capacity: 1800,
      boatLength: "15'-17'",
      overallLength: "19'10\"",
      bowEyeMin: "14'4\"",
      bowEyeMax: "17'10\"",
      gvwr: 2290,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "5.30 x 12 LRD 5H",
      overallWidth: "85\"",
      betweenFenders: "65\""
    },
    {
      model: "VASK-2100",
      capacity: 2100,
      boatLength: "15'-17'",
      overallLength: "21'8\"",
      bowEyeMin: "14'",
      bowEyeMax: "18'",
      gvwr: 2630,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "ST175/80R 13 LRC 5H",
      overallWidth: "89\"",
      betweenFenders: "70\""
    },
    {
      model: "VASK-2300",
      capacity: 2300,
      boatLength: "16'-18'",
      overallLength: "21'8\"",
      bowEyeMin: "14'",
      bowEyeMax: "18'",
      gvwr: 2830,
      tongueJack: "Optional",
      axles: 1,
      brakes: "Optional",
      tireSize: "ST185/80R 13 LRD 5H",
      overallWidth: "89\"",
      betweenFenders: "70\""
    },
    {
      model: "VASK-2500",
      capacity: 2500,
      boatLength: "17'-19'",
      overallLength: "21'8\"",
      bowEyeMin: "14'",
      bowEyeMax: "18'",
      gvwr: 3050,
      tongueJack: "Optional",
      axles: 1,
      brakes: "Optional",
      tireSize: "ST185/80R 13 LRD 5H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    },
    {
      model: "VASK-2800",
      capacity: 2800,
      boatLength: "18'-21'",
      overallLength: "23'6\"",
      bowEyeMin: "17'",
      bowEyeMax: "20'6\"",
      gvwr: 3380,
      tongueJack: "Optional",
      axles: 1,
      brakes: "Optional",
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    },
    {
      model: "VASK-3200",
      capacity: 3200,
      boatLength: "19'-21'",
      overallLength: "23'6\"",
      bowEyeMin: "17'",
      bowEyeMax: "20'6\"",
      gvwr: 3800,
      tongueJack: "Single Wheel",
      axles: 1,
      brakes: "Disc",
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    }
  ];

  // Aluminum Tandem Axle Skiff Trailers
  const aluminumTandemAxleSkiffModels: SkiffTrailerModel[] = [
    {
      model: "VASK-4200S",
      capacity: 4200,
      boatLength: "21'-22'",
      overallLength: "24'6\"",
      bowEyeMin: "17'",
      bowEyeMax: "21'",
      gvwr: 5040,
      tongueJack: "Single Wheel",
      axles: 2,
      brakes: "Disc-1",
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    },
    {
      model: "VASK-4200",
      capacity: 4200,
      boatLength: "22'-23'",
      overallLength: "26'6\"",
      bowEyeMin: "17'",
      bowEyeMax: "23'",
      gvwr: 5120,
      tongueJack: "Single Wheel",
      axles: 2,
      brakes: "Disc-1",
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    }
  ];

  // Galvanized Jon Boat Trailers
  const galvanizedJonBoatModels: SkiffTrailerModel[] = [
    {
      model: "VB-1000",
      capacity: 1000,
      boatLength: "12'-14'",
      overallLength: "15'9\"",
      bowEyeMin: "8'6\"",
      bowEyeMax: "13'",
      gvwr: 1270,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "4.80 x 12 LRC 5H",
      overallWidth: "60\"",
      betweenFenders: "42\""
    },
    {
      model: "VB-1300",
      capacity: 1300,
      boatLength: "14'-15'",
      overallLength: "17'6\"",
      bowEyeMin: "8'6\"",
      bowEyeMax: "15'6\"",
      gvwr: 1575,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "4.80 x 12 LRC 5H",
      overallWidth: "60\"",
      betweenFenders: "42\""
    },
    {
      model: "VB-1500",
      capacity: 1500,
      boatLength: "14'-15'",
      overallLength: "16'8\"",
      bowEyeMin: "10'6\"",
      bowEyeMax: "14'6\"",
      gvwr: 1870,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "5.30 x 12 LRC 5H",
      overallWidth: "78\"",
      betweenFenders: "60\""
    },
    {
      model: "VB-1800",
      capacity: 1800,
      boatLength: "15'-16'",
      overallLength: "19'2\"",
      bowEyeMin: "10'6\"",
      bowEyeMax: "16'6\"",
      gvwr: 2180,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "5.30 x 12 LRD 5H",
      overallWidth: "78\"",
      betweenFenders: "60\""
    }
  ];

  const skiffTrailerFeatures = [
    "Lightweight aluminum or galvanized steel construction",
    "Low profile design for easy loading and launching", 
    "Carpeted bunks to protect hull from scratches",
    "Load guides come standard on all skiff models",
    "Submersible LED lighting system",
    "Fits a wide range of small boat brands",
    "Single or tandem axle configurations available",
    "Plug & Play LED lighting system",
    "Aluminum diamond plate fenders",
    "Heavy duty winch straps and hardware",
    "D.O.T. composite brake lines with brass fittings",
    "Balanced radial tires with professional balancing"
  ];

  return (
    <div className="skiff-page">
      <SEOHead
        title="Skiff & Jon Boat Trailers - Shallow Draft Boats | Fast Boat Trailers"
        description="Skiff and jon boat trailers from Fast Boat Trailers in Edgewater, MD. Lightweight aluminum and galvanized trailers for flats boats, jon boats, and shallow water skiffs."
        keywords="skiff trailers, jon boat trailers Maryland, flats boat trailers, shallow draft trailers, aluminum skiff trailers"
        ogTitle="Skiff & Jon Boat Trailers - Shallow Draft Boats"
        ogDescription="Skiff and jon boat trailers for flats boats and shallow draft vessels. Lightweight aluminum and galvanized construction."
        ogImage="/images/trailers/skiff.jpg"
        canonical="https://fastboattrailers.com/trailers/skiff"
      />
      <div className="container">
        {/* Hero Section */}
        <div className="skiff-hero">
          <div className="hero-main">
            <div className="hero-content">
              <h1>Skiff & Jon Boat Trailers</h1>
              <h2>Perfect for Small Boats & Shallow Water Fishing</h2>
              <p>
                Perfect for small boats, our Skiff and Jon Boat Trailers are made to carry 
                lightweight loads for seamless loading and launching. These trailers are designed 
                specifically for shallow draft boats, making them ideal for fishing in shallow 
                waters, marshes, and small lakes.
              </p>
              
              <h3>Benefits of Skiff & Jon Boat Trailers</h3>
              <ul className="benefits-list">
                <li>Fits a wide range of small boat brands</li>
                <li>Great for lightweight boats under 25 feet</li>
                <li>Single or tandem axle options available</li>
                <li>All skiff models come standard with load guides</li>
              </ul>
            </div>
            <div className="hero-features">
              <h3>Standard Features</h3>
              <div className="features-compact">
                {skiffTrailerFeatures.map((feature, index) => (
                  <div key={index} className="feature-item-compact">
                    <div className="feature-icon-compact">✓</div>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Aluminum Jon Boat Trailers Section */}
        <div className="models-section">
          <h2>Aluminum Jon Boat Trailers</h2>
          
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/aluminum-jon-boat.jpg" alt="Aluminum Jon Boat Trailers" />
            </div>
            <div className="category-header">
              <h3>Aluminum Jon Boat Trailers (1,025-1,325 lbs)</h3>
              <p>Ultra-lightweight aluminum construction perfect for small jon boats. Designed for boats 12-15 feet with capacity ranges from 1,025 to 1,325 lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>Bow Eye Min</th>
                    <th>Bow Eye Max</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Axles</th>
                    <th>Brakes</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                    <th>Between Fenders</th>
                  </tr>
                </thead>
                <tbody>
                  {aluminumJonBoatModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.bowEyeMin}</td>
                      <td>{model.bowEyeMax}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.axles}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                      <td>{model.betweenFenders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Aluminum Single Axle Skiff Trailers Section */}
        <div className="models-section">
          <h2>Aluminum Single Axle Skiff Trailers</h2>
          
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/aluminum-single-axle-skiff.jpg" alt="Aluminum Single Axle Skiff Trailers" />
            </div>
            <div className="category-header">
              <h3>Single Axle Skiff Trailers (1,800-3,200 lbs)</h3>
              <p>Aluminum single axle design perfect for skiff boats 15-21 feet. Professional-grade construction with capacity ranges from 1,800 to 3,200 lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>Bow Eye Min</th>
                    <th>Bow Eye Max</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Axles</th>
                    <th>Brakes</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                    <th>Between Fenders</th>
                  </tr>
                </thead>
                <tbody>
                  {aluminumSingleAxleSkiffModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.bowEyeMin}</td>
                      <td>{model.bowEyeMax}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.axles}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                      <td>{model.betweenFenders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Aluminum Tandem Axle Skiff Trailers Section */}
        <div className="models-section">
          <h2>Aluminum Tandem Axle Skiff Trailers</h2>
          
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/aluminum-tandem-axle-skiff.jpg" alt="Aluminum Tandem Axle Skiff Trailers" />
            </div>
            <div className="category-header">
              <h3>Tandem Axle Skiff Trailers (4,200 lbs)</h3>
              <p>Heavy-duty tandem axle design for larger skiff boats 21-23 feet. Enhanced stability and load distribution with 4,200 lb capacity.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>Bow Eye Min</th>
                    <th>Bow Eye Max</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Axles</th>
                    <th>Brakes</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                    <th>Between Fenders</th>
                  </tr>
                </thead>
                <tbody>
                  {aluminumTandemAxleSkiffModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.bowEyeMin}</td>
                      <td>{model.bowEyeMax}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.axles}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                      <td>{model.betweenFenders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Galvanized Jon Boat Trailers Section */}
        <div className="models-section">
          <h2>Galvanized Jon Boat Trailers</h2>
          
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/galvanized-jon-boat.jpg" alt="Galvanized Jon Boat Trailers" />
            </div>
            <div className="category-header">
              <h3>Galvanized Jon Boat Trailers (1,000-1,800 lbs)</h3>
              <p>Hot-dip galvanized construction for maximum corrosion resistance. Perfect for jon boats 12-16 feet with capacity ranges from 1,000 to 1,800 lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>Bow Eye Min</th>
                    <th>Bow Eye Max</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Axles</th>
                    <th>Brakes</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                    <th>Between Fenders</th>
                  </tr>
                </thead>
                <tbody>
                  {galvanizedJonBoatModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.bowEyeMin}</td>
                      <td>{model.bowEyeMax}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.axles}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                      <td>{model.betweenFenders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="skiff-cta">
          <h2>Ready to Get Your Skiff or Jon Boat Trailer?</h2>
          <p>
            Find the perfect lightweight trailer for your small boat with professional-grade 
            features, load guides, and construction designed for shallow water fishing.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary">Get a quote</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkiffTrailersPage;